import axios from 'axios';
import { message } from 'antd';
import { syncSignedInHint } from '../utils/signedInHint';
import i18n from '../i18n';
import { ensureDeviceId, getDeviceIdSync, getDeviceMetaSync, warmDeviceId } from '../utils/deviceFingerprint';

// Token keys constants
const ACCESS_TOKEN_KEY = 'auth_token';  // Compatible with PHP session
const REFRESH_TOKEN_KEY = 'refresh_token';

// ---------------------------------------------------------------------------
// Global HTTP error toast deduplication.
//
// As of 2026-05-24 the backend started returning real HTTP status codes (401,
// 403, 4xx, 5xx) instead of always returning 200 with code-in-body. Many
// per-service `catch` blocks already display `error.response?.data?.error`
// toasts, so if the global interceptor also toasts we get duplicates.
//
// Strategy: the interceptor toasts under a stable `key` (status + url). antd's
// message API treats same-key toasts as the same toast, so a per-service
// follow-up `message.error(...)` within the dedup window will simply replace
// the global one (or be a no-op) rather than stack.
// ---------------------------------------------------------------------------
const GLOBAL_TOAST_DEDUP_MS = 1500;
const recentGlobalToasts = new Map(); // key -> timestamp

function globalToast(type, content, key) {
    const now = Date.now();
    const last = recentGlobalToasts.get(key) || 0;
    if (now - last < GLOBAL_TOAST_DEDUP_MS) return;
    recentGlobalToasts.set(key, now);
    // Cleanup old entries occasionally
    if (recentGlobalToasts.size > 50) {
        for (const [k, t] of recentGlobalToasts.entries()) {
            if (now - t > GLOBAL_TOAST_DEDUP_MS * 4) recentGlobalToasts.delete(k);
        }
    }
    try {
        message[type]({ content: i18n.t(content), key: type === 'error' ? ERROR_KEY : key, duration: 4 });
    } catch (_) {
        // antd not yet mounted (very early bootstrap); ignore
    }
}

// One error slot for the whole app: every message.error() without its own key shares ERROR_KEY,
// so when the interceptor and the page both report the same failure only one toast is visible
// (the later, usually more specific one, replaces the earlier). Callers that pass a key keep it.
const ERROR_KEY = 'yc-error';
if (!message.__ycErrorDedupe) {
    const originalError = message.error.bind(message);
    message.error = (content, duration, onClose) => {
        if (content && typeof content === 'object' && !Array.isArray(content) && 'content' in content) {
            return originalError({ key: ERROR_KEY, ...content });
        }
        return originalError({ content, key: ERROR_KEY, duration: typeof duration === 'number' ? duration : 4, onClose });
    };
    message.__ycErrorDedupe = true;
}

// Get API base URL from environment variable or fallback to default
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api2.gtm.cx';

// Build mode: 'combined' (dev), 'user', or 'admin'
const BUILD_MODE = process.env.REACT_APP_BUILD_MODE || 'combined';

// axios
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Flag to prevent multiple refresh attempts in parallel
let isRefreshing = false;
// Store pending requests that should be retried after token refresh
let failedQueue = [];
// Flag to prevent multiple 401 handling (redirect/logout)
let isLoggingOut = false;

// Rate limit handling
let isShowingCaptcha = false;
let captchaQueue = []; // Queue of requests waiting for captcha

// Custom error class for rate limit errors
class RateLimitError extends Error {
    constructor(data) {
        super('Rate Limited');
        this.name = 'RateLimitError';
        this.isRateLimit = true;
        this.retryAfter = data.retry_after;
        this.captchaRequired = data.captcha_required;
        this.captchaType = data.captcha_type;
    }
}

// Process captcha queue after successful verification
// Now uses inline verification - passes answer directly instead of pre-verified token
const processCaptchaQueue = (captchaId, captchaAnswer, captchaType = 'slider') => {
    captchaQueue.forEach(({ resolve, originalRequest }) => {
        // Add captcha headers to original request for inline verification
        originalRequest.headers['X-Captcha-Id'] = captchaId;
        originalRequest.headers['X-Captcha-Answer'] = captchaAnswer;
        originalRequest.headers['X-Captcha-Type'] = captchaType;
        resolve(apiClient(originalRequest));
    });
    captchaQueue = [];
};

// Reject all queued requests
const rejectCaptchaQueue = (error) => {
    captchaQueue.forEach(({ reject }) => {
        reject(error);
    });
    captchaQueue = [];
};

// Handle rate limit - show captcha modal
const handleRateLimit = (originalRequest, responseData) => {
    console.log('[RateLimit] handleRateLimit called, dispatching event...');
    return new Promise((resolve, reject) => {
        // Add to queue
        captchaQueue.push({ resolve, reject, originalRequest });
        console.log('[RateLimit] Added request to captcha queue, queue size:', captchaQueue.length);

        // If already showing captcha, just queue the request
        if (isShowingCaptcha) {
            console.log('[RateLimit] Captcha already showing, request queued');
            return;
        }

        isShowingCaptcha = true;

        // Dispatch event to show captcha modal
        console.log('[RateLimit] Dispatching ratelimit:captcha event');
        window.dispatchEvent(new CustomEvent('ratelimit:captcha', {
            detail: {
                retryAfter: responseData.retry_after,
                captchaType: responseData.captcha_type || 'slider',
                captchaTitle: responseData.captcha_title || 'Security Verification',
                captchaMessage: responseData.message || 'Please complete verification to continue',
                onSuccess: (captchaId, captchaAnswer, captchaType) => {
                    console.log('[RateLimit] Captcha success, retrying requests with inline verification...');
                    isShowingCaptcha = false;
                    processCaptchaQueue(captchaId, captchaAnswer, captchaType || responseData.captcha_type || 'slider');
                },
                onCancel: () => {
                    console.log('[RateLimit] Captcha cancelled');
                    isShowingCaptcha = false;
                    rejectCaptchaQueue(new RateLimitError(responseData));
                }
            }
        }));
    });
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });

    failedQueue = [];
};

// Compute the login URL with a return_to pointing at the user's current location
// so we can bounce them back after re-auth. We deliberately avoid redirecting
// the user to /login if they're ALREADY on /login (or /register / /reset-password)
// to prevent a tight redirect loop.
const buildLoginUrl = () => {
    const { pathname, search, hash } = window.location;
    // Don't capture return_to for auth pages — they're not useful post-login.
    const isAuthPage = /^\/(login|register|reset-password)(\/|$)/.test(pathname);
    if (isAuthPage) return '/login';
    const here = pathname + search + hash;
    return '/login?return_to=' + encodeURIComponent(here);
};

// Handle 401 logout - only execute once
const handleUnauthorizedLogout = () => {
    if (isLoggingOut) {
        return false; // Already handling logout, skip
    }
    isLoggingOut = true;

    // Clear all tokens
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('user_info');
            syncSignedInHint();

    // Dispatch logout event for any listeners
    window.dispatchEvent(new CustomEvent('auth:logout'));

    const loginUrl = buildLoginUrl();

    // Direct redirect to login page
    // Use setTimeout to ensure this runs after current call stack
    setTimeout(() => {
        window.location.href = loginUrl;
    }, 100);

    // Reset flag after a delay to allow future logins
    setTimeout(() => {
        isLoggingOut = false;
    }, 3000);

    return true; // First time handling
};

// Custom error class for silent 401 errors (no message display needed)
class SilentAuthError extends Error {
    constructor() {
        super('Unauthorized');
        this.name = 'SilentAuthError';
        this.isSilent = true;
        this.silent = true;
    }
}

// Promise that never resolves - used when redirecting to login
// This prevents error messages from showing before redirect completes
const neverResolve = () => new Promise(() => {});

// Anti-abuse device id (user console only): every request carries it once computed; sign-in,
// coupon, trial and order endpoints wait briefly for it so the server can record it.
const DEVICE_WAIT_URL = /(auth\/(register|login|2fa-verify|oauth\/exchange)|coupons\/redeem|cart\/checkout|cart\/validate-coupon|\/domains\b|\/zones\b)/;
const DEVICE_WAIT_MS = 1500;
if (BUILD_MODE !== 'admin') warmDeviceId();
const withDeviceHeaders = (config) => {
    const id = getDeviceIdSync();
    if (id) {
        config.headers['X-Device-Id'] = id;
        const meta = getDeviceMetaSync();
        if (meta && meta.length <= 1024) config.headers['X-Device-Meta'] = meta;
    }
    return config;
};

// 请求拦截器
const prepareRequest = (config) => {
        // 独立部署时去掉 /api/user 或 /api/admin 前缀（nginx rewrite 会自动补回）
        if (BUILD_MODE === 'user' && config.url?.startsWith('/api/user/')) {
            config.url = config.url.replace('/api/user', '');
        } else if (BUILD_MODE === 'admin' && config.url?.startsWith('/api/admin/')) {
            config.url = config.url.replace('/api/admin', '');
        }

        // 界面语言：后端据此翻译套餐名/地区名等目录文案（LocalizeCatalogMiddleware）；简体中文不发
        if (BUILD_MODE !== 'admin' && i18n.language && i18n.language !== 'zh-CN') {
            config.headers['X-UI-Lang'] = i18n.language;
        }

                // 禁用浏览器对 GET 请求的缓存，确保每次都获取最新数据
        if (!config.method || config.method.toLowerCase() === 'get') {
            config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
            config.headers['Pragma'] = 'no-cache';
        }

        // Get token - using auth_token as primary key for JWT compatibility
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
};

apiClient.interceptors.request.use(
    (config) => {
        if (BUILD_MODE !== 'admin') {
            const method = (config.method || 'get').toLowerCase();
            if (!getDeviceIdSync() && method !== 'get' && DEVICE_WAIT_URL.test(config.url || '')) {
                const timeout = new Promise((resolve) => setTimeout(resolve, DEVICE_WAIT_MS));
                return Promise.race([ensureDeviceId(), timeout])
                    .then(() => prepareRequest(withDeviceHeaders(config)), () => prepareRequest(config));
            }
            withDeviceHeaders(config);
        }
        return prepareRequest(config);
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 检查是否为401未授权错误（同时支持HTTP状态码和响应体中的code）
const isUnauthorizedError = (error) => {
    // HTTP 状态码 401
    if (error.response?.status === 401) return true;
    // 响应体中 code === 401
    if (error.response?.data?.code === 401) return true;
    // 响应体中 error === "Unauthorized" 且 code === 401
    if (error.response?.data?.error === 'Unauthorized') return true;
    return false;
};

// 响应拦截器
apiClient.interceptors.response.use(
    (response) => {
        // 检查成功响应中是否包含401错误（某些API可能返回200但body中有错误）
        if (response.data?.code === 401 || response.data?.error === 'Unauthorized') {
            const error = new Error('Unauthorized');
            error.response = response;
            return Promise.reject(error);
        }

        // 检查成功响应中是否包含429/430错误（rate limit）
        const code = response.data?.code;
        if (code === 429 || code === 430) {
            const responseData = response.data || {};
            console.log('[RateLimit] Detected rate limit in 200 response:', responseData);

            // If captcha is required
            if (responseData.captcha_required && !response.config._captchaRetry) {
                console.log('[RateLimit] Showing captcha modal...');
                response.config._captchaRetry = true;
                return handleRateLimit(response.config, responseData);
            }

            console.log('[RateLimit] No captcha required or already retried, rejecting...');
            return Promise.reject(new RateLimitError(responseData));
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // 如果已经在登出过程中，返回永不resolve的Promise（页面即将跳转）
        if (isLoggingOut && isUnauthorizedError(error)) {
            return neverResolve();
        }

        // 如果是 401 错误且不是刷新令牌的请求且未重试过
        if (isUnauthorizedError(error) &&
            !originalRequest._retry &&
            !originalRequest.url?.includes('auth/refresh-token')) {

            // 标记已经尝试重试
            originalRequest._retry = true;

            // 如果已经在刷新令牌中，将请求添加到队列
            if (isRefreshing) {
                try {
                    // 等待当前的刷新令牌请求完成
                    const token = await new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });

                    // 使用新令牌更新原始请求
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return apiClient(originalRequest);
                } catch (err) {
                    // 如果正在登出，返回永不resolve的Promise
                    if (isLoggingOut) {
                        return neverResolve();
                    }
                    return Promise.reject(err);
                }
            }

            // 标记开始刷新令牌
            isRefreshing = true;

            try {
                // 尝试刷新令牌
                const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
                if (!refreshToken) {
                    processQueue(new SilentAuthError());
                    isRefreshing = false;

                    // 处理未授权登出（会跳转到登录页）
                    handleUnauthorizedLogout();

                    // 返回永不resolve的Promise，防止错误显示
                    return neverResolve();
                }

                // 调用刷新令牌API
                const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
                    refresh_token: refreshToken
                });

                if (response.data.success && response.data.token) {
                    // 更新本地存储的令牌 - 兼容新的JWT格式
                    const newToken = response.data.token;
                    const newRefreshToken = response.data.refresh_token || refreshToken;

                    localStorage.setItem(ACCESS_TOKEN_KEY, newToken);
            syncSignedInHint();
                    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

                    // 处理队列中的请求
                    processQueue(null, newToken);

                    // 更新请求头并重试原始请求
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;

                    // 重置标志
                    isRefreshing = false;

                    return apiClient(originalRequest);
                } else {
                    // 刷新失败
                    processQueue(new SilentAuthError());
                    isRefreshing = false;

                    // 处理未授权登出
                    handleUnauthorizedLogout();

                    return neverResolve();
                }
            } catch (refreshError) {
                // 刷新过程中出错
                processQueue(new SilentAuthError());
                isRefreshing = false;

                // 处理未授权登出
                handleUnauthorizedLogout();

                return neverResolve();
            }
        }

        // Handle rate limit (429) and captcha required (430)
        const status = error.response?.status;
        const code = error.response?.data?.code;
        if (status === 429 || status === 430 || code === 429 || code === 430) {
            const responseData = error.response?.data || {};
            console.log('[RateLimit] Detected rate limit response:', { status, code, responseData });

            // If captcha is required and not already retrying
            if (responseData.captcha_required && !originalRequest._captchaRetry) {
                console.log('[RateLimit] Showing captcha modal...');
                originalRequest._captchaRetry = true;
                return handleRateLimit(originalRequest, responseData);
            }

            console.log('[RateLimit] No captcha required or already retried, rejecting...');
            // If no captcha required or already retried, reject with rate limit error
            return Promise.reject(new RateLimitError(responseData));
        }

        // ------------------------------------------------------------------
        // Global HTTP error UX (post 2026-05-24 — real status codes now)
        //
        // Opt-out: any request can set `config.skipGlobalErrorToast = true`
        // to disable the global toast (e.g. login form does its own messaging).
        // ------------------------------------------------------------------
        const skipGlobal = originalRequest && originalRequest.skipGlobalErrorToast;
        const url = (originalRequest && originalRequest.url) || '';

        if (!skipGlobal) {
            const body = error.response?.data;
            // Some callers expect raw `error` text on the error object so
            // their own .catch can read it. We also surface it to the user
            // ourselves where the status code makes the meaning clear.

            if (!error.response) {
                // No response at all → network error / CORS / timeout / aborted.
                // Don't toast aborted requests (user navigated away).
                const isAbort = error.code === 'ERR_CANCELED' || error.message === 'canceled';
                if (!isAbort) {
                    globalToast('error', 'Network error — please check your connection', 'net-err');
                }
            } else if (status === 403) {
                // Permission denied. Surface a clear message rather than the
                // generic "Request failed" the per-service catches show.
                const detail =
                    (body && (body.error || body.message)) ||
                    'You do not have permission to perform this action';
                globalToast('error', `Permission denied: ${detail}`, `403:${url}`);
            } else if (status >= 500) {
                // 5xx — never leak raw backend error text to the UI (could be
                // a stack trace or an internal exception message).
                globalToast('error', 'Server error — please try again in a moment', `5xx:${url}`);
            } else if (status >= 400 && status !== 401 && status !== 404) {
                // Other 4xx (400, 405, 409, 422, ...). 401 has its own flow,
                // 404 is often expected by services (e.g. probing existence)
                // so we leave it to the caller. Toast the body message.
                const detail =
                    (body && (body.error || body.message)) ||
                    `Request failed (${status})`;
                globalToast('error', detail, `${status}:${url}`);
            }
        }

        // Callers check this to avoid showing a second toast for the same failure.
        if (!skipGlobal && error.response && (error.response.status === 403 || error.response.status >= 400 && error.response.status !== 401 && error.response.status !== 404)) {
            error.toasted = true;
        } else if (!skipGlobal && !error.response && !(error.code === 'ERR_CANCELED' || error.message === 'canceled')) {
            error.toasted = true;
        }
        // 对于其他错误，直接拒绝
        return Promise.reject(error);
    }
);

// 导出
export { SilentAuthError, RateLimitError };
export default apiClient;
